import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getCantileverDocumentEntryId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { cantileverDocumentEntryId as deriveCantileverDocumentEntryId } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import {
  resolveDerivedOriginalFileRef,
  resolveDerivedPdfFileRef,
} from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'
import type { RolodexSignedURLResult } from 'types'

/**
 * Resolve PDF/original signed URLs for an open document tab.
 * File hosts UUID5-hash the BQ `document_id` (write-side identity), not the
 * tab navigation id. Filename still comes from the system-document loaded by
 * mixed navigation ids. Null-`document_id` docs fall back to the host tag.
 */
export function useCantileverHostsForDocumentTab(
  documentId: string,
  siteId: string
): {
  systemDocumentEntry: SystemDocumentEntry | undefined
  systemDocumentEntryId: string | undefined
  cantileverDocumentEntryId: string | undefined
  documentFilename: string | undefined
  documentSignedURL: RolodexSignedURLResult | undefined
  originalDocumentSignedURL: RolodexSignedURLResult | undefined
  totalPages: number
  isLoadingSelectedDocument: boolean
  isSelectedDocumentUnavailable: boolean
} {
  const { isPipelineConfigScopeLoading, selectedPipelineConfigId } =
    usePipelineConfig()

  const { document: systemDocumentEntry, isLoading: isLoadingSystemDocument } =
    useSystemDocumentByBqId(documentId)

  const systemDocumentEntryId = systemDocumentEntry?.entry.id
  const resolvedBqDocumentId = systemDocumentEntry
    ? getSystemDocumentBqId(systemDocumentEntry)
    : undefined
  const documentFilename = systemDocumentEntry?.entry.data.filename
  const taggedCantileverDocumentEntryId = systemDocumentEntry
    ? getCantileverDocumentEntryId(systemDocumentEntry)
    : undefined
  const cantileverDocumentEntryId = resolvedBqDocumentId
    ? deriveCantileverDocumentEntryId(siteId, resolvedBqDocumentId)
    : taggedCantileverDocumentEntryId
  const isLoadingSelectedDocument = isLoadingSystemDocument

  const matchesNavigationId =
    resolvedBqDocumentId === documentId ||
    systemDocumentEntry?.entry.id === documentId
  const isSelectedDocumentUnavailable =
    !!documentId &&
    !isPipelineConfigScopeLoading &&
    !!selectedPipelineConfigId &&
    !isLoadingSelectedDocument &&
    (systemDocumentEntry === undefined || !matchesNavigationId)

  const signedUrlQuery = useMemo(
    () =>
      resolveDerivedPdfFileRef({
        siteId,
        bqDocumentId: resolvedBqDocumentId,
        filename: documentFilename,
        cantileverDocumentEntryId: resolvedBqDocumentId
          ? undefined
          : taggedCantileverDocumentEntryId,
      }),
    [
      documentFilename,
      resolvedBqDocumentId,
      siteId,
      taggedCantileverDocumentEntryId,
    ]
  )

  const originalSignedUrlQuery = useMemo(
    () =>
      resolveDerivedOriginalFileRef({
        siteId,
        bqDocumentId: resolvedBqDocumentId,
        filename: documentFilename,
        cantileverDocumentEntryId: resolvedBqDocumentId
          ? undefined
          : taggedCantileverDocumentEntryId,
      }),
    [
      documentFilename,
      resolvedBqDocumentId,
      siteId,
      taggedCantileverDocumentEntryId,
    ]
  )

  const { results: documentSignedURLResults } =
    useRolodexSignedURLs(signedUrlQuery)
  const { results: originalDocumentSignedURLResults } = useRolodexSignedURLs(
    originalSignedUrlQuery
  )

  const totalPages = useMemo(() => {
    const pages = systemDocumentEntry?.entry.data?.total_pages ?? 0
    return pages === 0 ? 1 : pages
  }, [systemDocumentEntry])

  return {
    systemDocumentEntry,
    systemDocumentEntryId,
    cantileverDocumentEntryId,
    documentFilename,
    documentSignedURL: documentSignedURLResults?.[0],
    originalDocumentSignedURL: originalDocumentSignedURLResults?.[0],
    totalPages,
    isLoadingSelectedDocument,
    isSelectedDocumentUnavailable,
  }
}
